import React, {useEffect, useState} from 'react';
import {
    Box,
    TextField,
    Button,
    RadioGroup,
    FormControlLabel,
    Radio,
    InputLabel,
    Select,
    MenuItem,
    FormControl, Typography
} from '@mui/material';
import {
    updatePwdHash,
    getBookingConfig,
    updateBookingConfig,
    getSavedAccounts,
    deleteSavedAccount,
    getBannedEmails,
    addBannedAccount,
    deleteBannedAccount
} from './api'
import sha256 from "crypto-js/sha256";


export default function Admin() {
  const [selectedPwdKey, setSelectedPwdKey] = useState('user'); // default selection
  const [newPassword, setNewPassword] = useState('');
  const [bannedAccountInput, setBannedAccountInput] = useState('');
  const [savedAccounts, setSavedAccounts] = useState([]);
  const [bannedEmails, setBannedEmails] = useState([]);
  const [bookingConfig, setBookingConfig] = useState({
    debug: false,
    retries: 0,
    interval: 0,
    trigger_time: "00:00:00",
  });

  useEffect(() => {
    const fetchBookingConfig = async () => {
      const fetchedConfig = await getBookingConfig();
      setBookingConfig(fetchedConfig);
    };
    const fetchSavedAccounts = async () => {
      try {
        const accounts = await getSavedAccounts();
        setSavedAccounts(accounts);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    const fetchBannedEmails = async () => {
      try {
        const emails = await getBannedEmails();
        setBannedEmails(emails);
      } catch (error) {
        console.error('Failed to fetch accounts:', error);
      }
    };

    fetchSavedAccounts();
    fetchBannedEmails();
    fetchBookingConfig();
  }, []);

  const handleBookingConfigUpdate = async () => {
    const payload = {
      retries: bookingConfig.retries,
      interval: bookingConfig.interval,
      debug: bookingConfig.debug,
      trigger_time: bookingConfig.trigger_time,
    };

    try {
      const resp = await updateBookingConfig(payload);
      alert(resp.message);
    } catch (error) {
      console.error('Error updating booking config:', error);
      alert('Error updating booking config');
    }
  };

  const handlePasswordUpdate = async () => {
    if (!bookingConfig) {
      alert('Please enter a new password');
      return;
    }

    const payload = {
      key: selectedPwdKey,
      hash: sha256(newPassword).toString(),
    };

    try {
      const resp = await updatePwdHash(payload);
      alert(resp.message);
    } catch (error) {
      console.error('Error updating password:', error);
      alert('Error updating password');
    }
  };

  const handleRemoveSavedAccount = async (index) => {
    const accountToDelete = savedAccounts[index]

    const payload = {
      email: accountToDelete.email,
    };

    try {
      const res = await deleteSavedAccount(payload);
      if (res.success) {
        setSavedAccounts(savedAccounts.filter((_, i) => i !== index));
      }
      alert(res.message)
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error deleting account.');
    }
  };

  const handleRemoveBannedAccount = async (index) => {
    const bannedEmailToRemove = bannedEmails[index]

    const payload = {
      email: bannedEmailToRemove,
    };

    try {
      const res = await deleteBannedAccount(payload);
      if (res.success) {
        setBannedEmails(bannedEmails.filter((_, i) => i !== index));
      }
      alert(res.message)
    } catch (err) {
      console.error('Delete error:', err);
      alert('Error removing banned account.');
    }
  };

  const handleBanAccount = async () => {
    if (!bannedAccountInput) {
      alert('Please enter an email that you want to ban.');
      return;
    }

    const payload = {
      email: bannedAccountInput,
    };

    try {
      const resp = await addBannedAccount(payload);
      if (resp.success) {
          setBannedEmails(prev => [...prev, bannedAccountInput]);
          setBannedAccountInput('')
      }
      alert(resp.message);
    } catch (error) {
      console.error('Error banning account:', error);
      alert('Error banning account.');
    }
  };

  return (
      <Box sx={{ padding: 4 }}>

        <Box className="header-box box">
          <h1>Admin Settings</h1>
          <p>Here you can manage the AutoCourt app using the provided admin dashboard.</p>
        </Box>

        <Box className="flex-row" sx={{ display: 'flex', gap: 3 }}>
            <Box className="booking-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 }}>
              <h2>Password Settings</h2>
              <TextField
                label="New Password"
                name="new-pwd"
                type="text"
                onChange={(e) => setNewPassword(e.target.value)}
                fullWidth
                sx={{ marginBottom: 2 }}
              />

              <RadioGroup
                row
                name="user-select"
                value={selectedPwdKey}
                onChange={(e) => setSelectedPwdKey(e.target.value)}
                sx={{
                  marginBottom: 2,
                  color: 'black',
                  '& .MuiRadio-root': {
                    color: 'black',
                  },
                  '& .Mui-checked': {
                    color: 'black',
                  }
                }}
              >
                <FormControlLabel value="user" control={<Radio />} label="User" />
                <FormControlLabel value="admin" control={<Radio />} label="Admin" />
              </RadioGroup>

              <Button
                variant="outlined"
                color="inherit"
                onClick={handlePasswordUpdate}
                fullWidth
                sx={{
                  fontFamily: "'Courier New', Courier, monospace",
                  borderColor: 'currentColor',
                }}
               >
                Update Password
              </Button>

              <hr style={{border:'1px solid #ddd', marginBottom: 40, marginTop: 40}}/>

              <h2>Booking Configuration</h2>
              <TextField
                label="Retry Interval (minutes)"
                name="retry-interval"
                type="number"
                onChange={(e) => setBookingConfig({
                  interval: e.target.value,
                  retries: bookingConfig.retries,
                  debug: bookingConfig.debug,
                  trigger_time: bookingConfig.trigger_time
                })}
                value={bookingConfig.interval ?? 0}
                fullWidth
                sx={{ marginBottom: 2 }}
                inputProps={{ min: 0, step: 1 }}
              />

              <TextField
                label="Number of Retires"
                name="max-retires"
                type="number"
                value={bookingConfig.retries ?? 0}
                onChange={(e) => setBookingConfig({
                  interval: bookingConfig.interval,
                  retries: e.target.value,
                  debug: bookingConfig.debug,
                  trigger_time: bookingConfig.trigger_time
                })}
                fullWidth
                sx={{ marginBottom: 2 }}
                inputProps={{ min: 0, step: 1 }}
              />
              <TextField
                label="Trigger Time"
                name="trigger-time"
                type="time"
                value={bookingConfig.trigger_time ?? 0}
                onChange={(e) => setBookingConfig({
                  interval: bookingConfig.interval,
                  retries: bookingConfig.retries,
                  debug: bookingConfig.debug,
                  trigger_time: e.target.value,
                })}
                fullWidth
                sx={{ marginBottom: 2 }}
                inputProps={{ min: 0, step: 1 }}
              />


              <FormControl fullWidth sx={{ marginBottom: 2 }}>
                <InputLabel>Debug Mode</InputLabel>
                <Select
                  name="debug_mode"
                  value={bookingConfig.debug ?? 'False'}
                  onChange={(e) => setBookingConfig({
                    interval: bookingConfig.interval,
                    retries: bookingConfig.retries,
                    debug: e.target.value,
                    trigger_time: bookingConfig.trigger_time
                  })}
                >
                  <MenuItem value={true}>On</MenuItem>
                  <MenuItem value={false}>Off</MenuItem>
                </Select>
              </FormControl>

              <Button
                variant="outlined"
                color="inherit"
                onClick={handleBookingConfigUpdate}
                fullWidth
                sx={{
                  fontFamily: "'Courier New', Courier, monospace",
                  borderColor: 'currentColor',
                }}
               >
                Update Config
              </Button>
            </Box>

            <Box className="booking-box" sx={{ flex: 1, padding: 2, border: '1px solid #ddd', borderRadius: 2 }}>
              <h2>Stored Accounts</h2>
                {savedAccounts.length > 0 ? (
                  savedAccounts.map((account, index) => (
                    account.email?.trim() ? (
                      <Box
                        key={index}
                        sx={{
                          border: '1px solid #ccc',
                          borderRadius: 2,
                          padding: 2,
                          mb: 2,
                          backgroundColor: '#e0e0e0',
                          display: "flex",
                          flexDirection: "row",
                          justifyContent: "space-between"
                        }}
                      >
                        <Typography
                          sx={{
                            fontFamily: "'Courier New', Courier, monospace",
                            marginBottom: 1,
                            marginTop: 1
                          }}
                        >
                          {account.email}
                        </Typography>
                        <Button
                          variant="outlined"
                          color="inherit"
                          sx={{fontFamily: "'Courier New', Courier, monospace"}}
                          onClick={() => handleRemoveSavedAccount(index)}
                        >
                          Remove
                        </Button>
                      </Box>
                    ) : null
                  ))
                ) : (
                  <Typography>No saved accounts.</Typography>
                )}

              <hr style={{border:'1px solid #ddd', marginBottom: 40, marginTop: 40}}/>

              <h2>Banned Accounts</h2>
                {bannedEmails.length > 0 ? (
                  bannedEmails.map((mail, index) => (
                  <Box
                    key={index}
                    sx={{
                      border: '1px solid #ccc',
                      borderRadius: 2,
                      padding: 2,
                      mb: 2,
                      backgroundColor: '#e0e0e0',
                      display: "flex",
                      flexDirection: "row",
                      justifyContent: "space-between"
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "'Courier New', Courier, monospace",
                        marginBottom: 1,
                        marginTop: 1
                      }}
                    >
                      {mail}
                    </Typography>
                    <Button
                      variant="outlined"
                      color="inherit"
                      sx={{fontFamily: "'Courier New', Courier, monospace"}}
                      onClick={() => handleRemoveBannedAccount(index)}
                    >
                      Remove
                    </Button>
                  </Box>
                  ))
                ) : (
                  <Typography>No banned accounts.</Typography>
                )}
                <TextField
                    label="Email"
                    name="ban-email"
                    type="email"
                    onChange={(e) => setBannedAccountInput(e.target.value)}
                    value={bannedAccountInput}
                    fullWidth
                    sx={{ marginBottom: 2, marginTop: 2 }}
                  />

                  <Button
                    variant="outlined"
                    color="inherit"
                    onClick={handleBanAccount}
                    fullWidth
                    sx={{
                      fontFamily: "'Courier New', Courier, monospace",
                      borderColor: 'currentColor',
                    }}
                   >
                    Ban Account
                  </Button>
            </Box>
        </Box>
      </Box>
  );
}


